import { useSearchParams } from 'react-router';
import MyReportedIncidents from '../../views/MyReportedIncidents';
import { useApiClients } from '../../client/api/useApiClient';
import {
  Department,
  GetAllDepartmentResponse,
  GetAllIncidentTypesResponse,
  GetUserIncidentsResponse,
  IncidentsSeverityEnum,
  IncidentsStatusEnum,
  IncidentTypes,
} from '@EHSNavigator/backend';
import { useQuery } from '@tanstack/react-query';
import Loader from '../../components/Loader';

export default function MyReportedIncidentsPage() {
  const { DepartmentApi, IncidentReportingApi } = useApiClients();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const currentPage = searchParams.get('page') ?? 1;
  const department = searchParams.get('department') ?? '';
  const type = searchParams.get('type') ?? '';
  const severity = searchParams.get('severity') ?? '';
  const status = searchParams.get('status') ?? '';
  const limit = searchParams.get('limit') ?? 10;

  const query: any = {
    page: Number(currentPage) || 1,
  };

  if (searchQuery) query.search = searchQuery;
  if (department) query.departmentId = department;
  if (status) query.status = status as IncidentsStatusEnum;
  if (type) query.incidentTypeId = type;
  if (severity) query.severity = severity as IncidentsSeverityEnum;
  if (limit) query.limit = Number(limit);

  const {
    data: reportedIncidents,
    isLoading,
    isRefetching,
  } = useQuery<GetUserIncidentsResponse, Error, any>({
    queryKey: [
      'all-incidents',
      searchQuery,
      currentPage,
      department,
      type,
      severity,
      status,
      Number(limit),
    ],
    queryFn: async () => {
      const response =
        await IncidentReportingApi.incidentReportingControllerGetUserIncidents(
          query,
        );
      return response?.data || [];
    },
  });

  const { data: allDepartments, isLoading: departmentsLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['my-reported-departments'],
      queryFn: async () => {
        const response = await DepartmentApi.departmentControllerFindAllDepartment({
          disablePagination: true,
        });
        return response?.data;
      },
    });
  const { data: incidentTypesResponse, isLoading: incidentTypesLoading } =
    useQuery<GetAllIncidentTypesResponse>({
      queryKey: ['incident-types'],
      queryFn: async () => {
        const response =
          await IncidentReportingApi.incidentReportingControllerFindAllIncidentTypes();
        return response?.data;
      },
    });

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const incidentTypes =
    incidentTypesResponse?.incidentTypes?.map((node: IncidentTypes) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const hasFilters =
    !!searchQuery || !!department || !!status || !!type || !!severity;

  const noDataMessage = hasFilters
    ? 'No items match your filters or search. Try using different keywords maybe.'
    : "You haven't reported an incident yet";

  if (incidentTypesLoading || departmentsLoading) {
    return <Loader />;
  }
  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <MyReportedIncidents
        params={{
          search: searchQuery,
          page: Number(currentPage),
          departmentId: department,
          status: status as IncidentsStatusEnum,
          severity: severity as IncidentsSeverityEnum,
          incidentTypeId: type,
          limit: Number(limit),
        }}
        isIncidentsLoading={isLoading || isRefetching}
        departments={departments}
        incidentTypes={incidentTypes}
        data={reportedIncidents}
        noDataMessage={noDataMessage}
      />
    </div>
  );
}
